<script lang="ts">
	//import { onMount } from 'svelte'
	import { goto } from '$app/navigation'
	import { sendGTM } from '$lib/analytics'
	import { CONTACT_VARS } from '$lib/consts'
	import { t } from '$lib/translations'
	import ContactField from './contact-field/ContactField.svelte'
	import FormTabs from './tabs'
	import { PUBLIC_WOLTAIR_WEB_CODE } from '$env/static/public'

	export let title: string | undefined = t('web.homepage.woltadvisorHeader')
	export let showHVAC: boolean = true
	export let showHiddenTabs: boolean = true
	export let showPV: boolean = false

	let selectedSegment = 0
	let insulationChanged: boolean = false
	let sourceChanged: boolean = false
	const contactVars = CONTACT_VARS[PUBLIC_WOLTAIR_WEB_CODE] || CONTACT_VARS['WOLTAIR_CZ']
	$: type = selectedSegment === 0 ? 'hvac' : 'pv'

	type HvacForm = {
		persons?: string | null
		floorArea?: string | null
		insulation?: string | null
		zipCode?: string | null
	}

	type PvForm = {
		houseHeatType?: string | null
		annualConsumption?: string | null
		zipCode?: string | null
	}

	const hvacForm: HvacForm = {
		persons: null,
		floorArea: null,
		zipCode: null
	}

	const pvForm: PvForm = {
		annualConsumption: null,
		zipCode: null
	}

	async function handleSubmit(event: Record<string, any>) {
		event.preventDefault()

		if (selectedSegment === 0) {
			sendGTM(
				'mini_calculator_submit_hvac',
				{},
				{
					calculator: {
						category: type,
						floor_area: hvacForm.floorArea,
						thermal_insulation: hvacForm.insulation,
						zip_code: hvacForm.zipCode,
						persons: hvacForm.persons
					},
					_clear: true
				}
			)

			await goto(
				`/marketplace?formType=${type}&peopleCount=${hvacForm.persons}&floorSquare=${hvacForm.floorArea}&isoLevel=${hvacForm.insulation}&cityName=${hvacForm.zipCode}`,
				{ invalidateAll: true }
			)
		} else {
			sendGTM(
				'mini_calculator_submit_fve',
				{},
				{
					calculator: {
						category: type,
						heating_source: pvForm.houseHeatType,
						annual_consumption: pvForm.annualConsumption,
						zip_code: pvForm.zipCode
					},
					_clear: true
				}
			)

			await goto(
				`/marketplace?formType=${type}&annualElectricityConsumption=${pvForm.annualConsumption}&houseHeatType=${pvForm.houseHeatType}&cityName=${pvForm.zipCode}`,
				{ invalidateAll: true }
			)
		}
	}
</script>

<div class="woltadvisor-calc h-fit bg-w-blue-950 px-0 py-10 sm:py-10">
	<div class="heading-container flex w-full justify-center px-10 sm:justify-start">
		<h2
			class="unstyled mt-0 text-center text-4xl-res font-semibold text-white sm:text-left sm:text-3.5xl"
		>
			{@html title}
		</h2>
	</div>
	<div class="relative w-full">
		<FormTabs.Tabs justify="center" bind:selectedTab={selectedSegment} showTabs={showHiddenTabs}>
			{#if showHVAC || showHiddenTabs}
				<FormTabs.TabItem title={t('web.forms.heatPump')} icon="/images/icons/white/heat_pump.svg">
					{#if showHVAC}
						<form on:submit|preventDefault={handleSubmit} id="heat-pump" class="px-2 sm:px-10">
							<label
								for="floor-area"
								class="grid w-full items-center overflow-hidden rounded-t-md border-b border-neutral-200 bg-white ring-inset ring-w-red-500/50 focus-within:ring-2 sm:rounded-t-lg"
							>
								<span
									class="z-10 col-start-1 row-start-1 mx-4 flex h-[2em] w-fit items-center whitespace-nowrap bg-white text-sm text-w-blue-950/70"
									>{@html t('web.forms.floorArea')}</span
								>
								<input
									id="floor-area"
									bind:value={hvacForm.floorArea}
									class="col-start-1 row-start-1 mt-auto w-full border-0 bg-transparent p-0 px-4 py-4 text-right text-lg font-medium text-w-blue-950 placeholder:font-normal placeholder:text-w-blue-950/40 focus:ring-0"
									placeholder="100"
								/>
							</label>

							<label
								for="thermal-insulation"
								class="grid w-full items-center border-b border-neutral-200 bg-white ring-inset ring-w-red-500/50 focus-within:ring-2"
							>
								<span
									class="z-10 col-start-1 row-start-1 mx-4 flex h-[2em] w-fit items-center whitespace-nowrap bg-white text-sm text-w-blue-950/70"
									>{t('web.forms.thermalInsulation')}</span
								>
								<select
									id="thermal-insulation"
									bind:value={hvacForm.insulation}
									on:change={() => (insulationChanged = true)}
									class="text-align-last-right col-start-1 row-start-1 mt-auto w-full cursor-pointer border-0 bg-transparent p-0 px-4 py-4 pr-10 text-right text-lg {insulationChanged
										? ' font-medium text-w-blue-950'
										: 'text-w-blue-950/40'} focus:ring-0"
								>
									<option disabled selected value="null">{t('web.forms.choose')}</option>
									<option value="NO" class="text-neutral-900"
										>{t('web.forms.thermalInsulation.no')}</option
									>
									<option value="LOW" class="text-neutral-900"
										>{t('web.forms.thermalInsulation.low')}</option
									>
									<option value="MEDIUM" class="text-neutral-900"
										>{t('web.forms.thermalInsulation.medium')}</option
									>
									<option value="HIGH" class="text-neutral-900"
										>{t('web.forms.thermalInsulation.high')}</option
									>
								</select>
							</label>

							<label
								for="floor-area"
								class="grid w-full items-center border-b border-neutral-200 bg-white ring-inset ring-w-red-500/50 focus-within:ring-2"
							>
								<span
									class="z-10 col-start-1 row-start-1 mx-4 flex h-[2em] w-fit items-center whitespace-nowrap bg-white text-sm text-w-blue-950/70"
									>{t('web.forms.zipcode')}</span
								>
								<input
									id="zip-code"
									bind:value={hvacForm.zipCode}
									placeholder="11000"
									class="col-start-1 row-start-1 mt-auto w-full border-0 bg-transparent p-0 px-4 py-4 text-right text-lg font-medium text-w-blue-950 placeholder:font-normal placeholder:text-w-blue-950/40 focus:ring-0"
								/>
							</label>

							<label
								for="persons"
								class="grid w-full items-center overflow-hidden rounded-b-md bg-white ring-inset ring-w-red-500/50 focus-within:ring-2 sm:rounded-b-lg"
							>
								<span
									class="z-10 col-start-1 row-start-1 mx-4 flex h-[2em] w-fit items-center whitespace-nowrap bg-white text-sm text-w-blue-950/70"
									>{t('web.forms.personsCount')}</span
								>
								<input
									id="persons"
									bind:value={hvacForm.persons}
									placeholder="3"
									class="col-start-1 row-start-1 mt-auto w-full border-0 bg-transparent p-0 px-4 py-4 text-right text-lg font-medium text-w-blue-950 placeholder:font-normal placeholder:text-w-blue-950/40 focus:ring-0"
								/>
							</label>

							<button
								type="submit"
								class="btn-new btn-new-red mt-4 w-full whitespace-normal rounded-md sm:mt-6 sm:rounded-lg"
								>{t('web.forms.showHeatpumps')}
								<!--
									RE-ENABLE WHEN WE HAVE WOLTADVISOR LOGIC FOR THIS COMPONENT DECIDED AND IMPLEMENTED
									{#if numberOfResults}({numberOfResults}){/if}--></button
							>
						</form>
					{:else if showHiddenTabs}
						<div class="w-full px-6 text-center sm:px-10">
							<p class="unstyled">{@html t('web.forms.HVACnotAvailable.message')}</p>
							<p class="unstyled mb-6 pt-2 text-sm font-light opacity-70">
								{@html t('web.forms.PVnotAvailable.subtext')}
							</p>
							<ContactField type="HVAC" />
						</div>
					{/if}
				</FormTabs.TabItem>
			{/if}
			{#if showPV || showHiddenTabs}
				<FormTabs.TabItem title={t('web.forms.photovoltaics')} icon="/images/icons/white/flash.svg">
					{#if showPV}
						<form on:submit|preventDefault={handleSubmit} id="heat-pump" class="px-2 sm:px-10">
							<label
								for="heating-source"
								class="relative grid h-20 w-full items-center rounded-t-md border-b border-neutral-200 bg-white ring-inset ring-w-red-500/50 focus-within:ring-2 sm:h-fit"
							>
								<span
									class="absolute top-2 z-10 col-start-1 row-start-1 mx-3 flex h-[2em] w-fit items-center whitespace-nowrap bg-white text-sm text-w-blue-950/70 sm:relative sm:top-0 sm:mx-4"
									>{t('web.woltadvisor.houseHeatType')}</span
								>
								<select
									id="heating-source"
									bind:value={pvForm.houseHeatType}
									on:change={() => (sourceChanged = true)}
									class="text-align-last-right col-start-1 row-start-1 mt-auto w-full cursor-pointer border-0 bg-transparent p-0 px-4 py-4 pr-10 text-right text-base font-medium {sourceChanged
										? 'font-medium text-w-blue-950'
										: 'text-w-blue-950/40'} focus:ring-0
								sm:text-lg"
								>
									<option disabled selected value="null">{t('web.forms.choose')}</option>
									<option value="COAL" class="text-neutral-900"
										>{t('web.woltadvisor.houseHeatType.COAL')}</option
									>
									<option value="COAL_I_II" class="text-neutral-900"
										>{t('web.woltadvisor.houseHeatType.COAL_I_II')}</option
									>
									<option value="ELECTRO" class="text-neutral-900"
										>{t('web.woltadvisor.houseHeatType.ELECTRO')}</option
									>
									<option value="GAS" class="text-neutral-900"
										>{t('web.woltadvisor.houseHeatType.GAS')}</option
									>
									<option value="GAS_CONDENSING" class="text-neutral-900"
										>{t('web.woltadvisor.houseHeatType.GAS_CONDENSING')}</option
									>
									<option value="HEATPUMP" class="text-neutral-900"
										>{t('web.woltadvisor.houseHeatType.HEATPUMP')}</option
									>
								</select>
							</label>

							<label
								for="persons"
								class="grid w-full items-center overflow-hidden border-b border-neutral-200 bg-white ring-inset ring-w-red-500/50 focus-within:ring-2"
							>
								<span
									class="z-10 col-start-1 row-start-1 mx-4 flex h-[2em] w-fit items-center whitespace-nowrap bg-white text-sm text-w-blue-950/70"
									>{t('web.forms.annualConsumption.withMwh')}</span
								>
								<input
									id="persons"
									bind:value={pvForm.annualConsumption}
									placeholder="7"
									class="col-start-1 row-start-1 mt-auto w-full border-0 bg-transparent p-0 px-4 py-4 text-right text-lg font-medium text-w-blue-950 placeholder:font-normal placeholder:text-w-blue-950/40 focus:ring-0"
								/>
							</label>

							<label
								for="floor-area"
								class="grid w-full items-center rounded-b-md border-b border-neutral-200 bg-white ring-inset ring-w-red-500/50 focus-within:ring-2 sm:rounded-b-lg"
							>
								<span
									class="z-10 col-start-1 row-start-1 mx-4 flex h-[2em] w-fit items-center whitespace-nowrap bg-white text-sm text-w-blue-950/70"
									>{t('web.forms.zipcode')}</span
								>
								<input
									id="zip-code"
									bind:value={pvForm.zipCode}
									placeholder="11000"
									class="col-start-1 row-start-1 mt-auto w-full border-0 bg-transparent p-0 px-4 py-4 text-right text-lg font-medium text-w-blue-950 placeholder:font-normal placeholder:text-w-blue-950/40 focus:ring-0"
								/>
							</label>

							<button
								type="submit"
								class="btn-new btn-new-red mt-4 w-full whitespace-normal rounded-md sm:mt-6 sm:rounded-lg"
								>{t('web.forms.showPhotovoltaics')}</button
							>
						</form>
					{:else if showHiddenTabs}
						<div class="w-full px-6 text-center sm:px-10">
							<p class="unstyled">{@html t('web.forms.PVnotAvailable.message')}</p>
							<p class="unstyled mb-6 pt-2 text-sm font-light opacity-70">
								{@html t('web.forms.PVnotAvailable.subtext')}
							</p>
							<ContactField type="PV" />
						</div>
					{/if}
				</FormTabs.TabItem>
			{/if}
		</FormTabs.Tabs>
	</div>
	<p class="unstyled w-full px-10 text-center text-sm">
		{@html t('web.forms.callUs', {
			phone: `<a class="whitespace-nowrap text-w-red-500" href="${contactVars.phoneRef}">${contactVars.phoneNum}</a>`,
			email: `<a href="mailto:${contactVars.mail}" class="text-w-red-500">${contactVars.mail}</a>`
		})}
	</p>
</div>

<style lang="scss">
	.text-align-last-right {
		text-align-last: right;
	}
	.woltadvisor-calc {
		display: flex;
		flex-direction: column;
		align-items: flex-start;
		justify-content: flex-start;
		gap: 30px;
		border-radius: 16px;
		overflow: hidden;
		color: #fff;
		width: 100%;
		max-width: 550px;

		@screen sm {
			width: 550px;
		}
	}
</style>
